/**
 * Shared field primitives used across the BookingForm tab sub-components.
 *
 * All exports here are stateless — they take `value` + `onChange` (or a
 * `set(key, value)` callback) and render. No hooks, no fetches.
 */
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import HelpTip from "@/components/HelpTip";

export function Field({ label, required, hint, children }) {
  return (
    <div className="space-y-1.5">
      <div className="flex items-center gap-1.5">
        <Label className="text-xs uppercase tracking-wider text-slate-500">
          {label}
          {required && (
            <span className="text-rose-600 ml-0.5" aria-label="required" title="Required field">*</span>
          )}
        </Label>
        {hint ? <HelpTip>{hint}</HelpTip> : null}
      </div>
      {children}
    </div>
  );
}

/** Generic Select fed by a loaded picklist (array of {value,label}). */
export function PickSelect({ picks, value, onChange, testid, placeholder = "Select…" }) {
  const options = picks || [];
  return (
    <Select value={value || "_none_"} onValueChange={(v) => onChange(v === "_none_" ? "" : v)}>
      <SelectTrigger data-testid={testid}>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="_none_">— None —</SelectItem>
        {options.map((o) => (
          <SelectItem key={o.value} value={o.value}>
            {o.label || o.value}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

/** Tiny red dot indicator for tabs with missing mandatory fields. */
export function Dot({ testId }) {
  return <span className="ml-1 inline-block w-1.5 h-1.5 rounded-full bg-rose-500 align-middle" title="Required fields still empty" data-testid={testId} />;
}

/** Chip-style multi-select that writes/reads a TEXT[] array column. */
export function MultiSelectChips({ label, options, value, onChange, testid }) {
  const has = (v) => value.includes(v);
  const toggle = (v) => onChange(has(v) ? value.filter((x) => x !== v) : [...value, v]);
  return (
    <div className="space-y-1.5">
      <Label className="text-xs uppercase tracking-wider text-slate-500">{label}</Label>
      <div className="flex flex-wrap gap-2" data-testid={testid}>
        {options.length === 0 && (
          <span className="text-xs text-slate-400">No options available</span>
        )}
        {options.map((o) => (
          <Badge
            key={o.value}
            variant={has(o.value) ? "default" : "outline"}
            className={`cursor-pointer transition ${has(o.value) ? "" : "hover:bg-slate-100"}`}
            onClick={() => toggle(o.value)}
            data-testid={`${testid}-chip-${o.value}`}
          >
            {o.label}
          </Badge>
        ))}
      </div>
    </div>
  );
}

/**
 * ConfirmCard — read-only summary of one tab's values + a single checkbox
 * the FLD ticks to confirm "matches the load on the dock". Stored as a
 * boolean in `qa_confirmations[tab]` on the booking row.
 */
export function ConfirmCard({ tab, title, confirmations, onConfirm, rows }) {
  const confirmed = !!(confirmations && confirmations[tab]);
  return (
    <div
      data-testid={`qa-confirm-${tab}`}
      className={`rounded-md border p-3 ${confirmed ? "bg-emerald-50 border-emerald-200" : "bg-white"}`}
    >
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-sm font-semibold">{title}</h4>
        <label className="flex items-center gap-2 text-xs">
          <Checkbox
            checked={confirmed}
            onCheckedChange={(v) => onConfirm(!!v)}
            data-testid={`qa-confirm-${tab}-checkbox`}
          />
          <span className={confirmed ? "text-emerald-700 font-medium" : "text-slate-500"}>
            {confirmed ? "Confirmed" : "Confirm matches"}
          </span>
        </label>
      </div>
      <dl className="grid grid-cols-2 sm:grid-cols-3 gap-x-3 gap-y-1 text-xs">
        {rows.map(([label, value]) => (
          <div key={label}>
            <dt className="uppercase tracking-wider text-slate-400">{label}</dt>
            <dd className="font-medium text-slate-800 break-words">{value}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}
